import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Drawer } from '@mui/material';
import { toast } from 'react-toastify';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

// components
import ProjectAddTask from './ProjectAddTask';

import { isAdmin, isClient, isStakeholder } from '../../../../../utils/share';
import { getAllTasksByProjectId, getTaskById } from '../../../../../services/taskService';
import { boldHeader, TASK_LIST_TABLE_COLUMNS, TASK_LIST_TABLE_DEFAULT_PROPS } from './constants';
import DeleteModal from './DeleteModal';
import TopToolBar from './TopToolBar';
import Actions from './Actions';
import TableWrapper from '../../../../../common/TableWrapper';
import { getTaskClassificationList } from '../../../../../services/taskClassification';
import { useProject } from '../../../Layout/header/context/index';
import { Loader } from '../../../../../components/loader';
import { EDIT_PARENT_TASK } from '../../../constants/index';

const ProjectTaskListTable = ({ projectName }) => {
  const { id } = useParams(); // Extracting project ID from URL parameters

  // State variables to manage the component's behavior
  const [action, setAction] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState();
  const [deleteSelected, setDeleteSelected] = useState();
  const [classificationsOption, setClassificationsOption] = useState([]);
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);

  // Variables for managing the table data asynchronously
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const accountData = useSelector((state) => state?.login?.user);
  const { projectDetails, isEditPermission, projectRole } = useProject();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const canAddParentTask = isAdmin() || EDIT_PARENT_TASK.includes(projectRole);

  useEffect(() => {
    // fetch project all classifications
    const fetchProjectClassifications = async () => {
      try {
        const response = await getTaskClassificationList({ pageSize: 'all', projectId: id });
        setClassificationsOption(response?.data?.data?.rows || []);
      } catch (error) {
        toast.error('An error occurred while fetching the classification list. Please try again later.');
      }
    };

    fetchProjectClassifications();
  }, [id]);

  // handles the asynchronous api calls
  const {
    data: { count, rows } = { count: 0, rows: [] },
    isError,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: [columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting],
    queryFn: async () => {
      try {
        const response = await getAllTasksByProjectId({
          start: pagination.pageIndex * pagination.pageSize,
          pageSize: pagination.pageSize,
          projectId: id,
          filters: JSON.stringify(columnFilters ?? []),
          globalFilter,
          sorting: JSON.stringify(sorting ?? []),
          assignedToMe: isStakeholder() || undefined,
        });
        return response?.data;
      } catch (error) {
        toast.error('An error occurred while fetching the task list. Please try again later.');
      }
      return { count: 0, rows: [] };
    },
    placeholderData: keepPreviousData,
  });

  const onDeleteIconClick = (data) => {
    if (parseInt(data?.consumedHours, 10) <= 0) {
      setDeleteSelected(data);
    }
  };

  // Handling task deletion
  const onTaskDelete = () => {
    try {
      setDeleteSelected(undefined);
      refetch();
    } catch (error) {
      toast.error(error?.response?.data?.message ?? error.message);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteSelected(undefined);
  };

  // Handling task creation
  const handleCreate = () => {
    if (projectDetails.allowManualTask || isEditPermission || projectRole === 'QA' || isClient()) {
      setAction('create');
      setIsDrawerLoading(true);
      handleDrawerOpen();
      setInterval(() => {
        setIsDrawerLoading(false);
      }, 500);

      return;
    }
    if (!isAdmin() && !isStakeholder()) {
      return;
    }
    setAction('create');
    setIsDrawerLoading(false);
    handleDrawerOpen();
  };

  // Opening the drawer
  const handleDrawerOpen = () => {
    setIsOpen(true);
  };

  const handleEditCellClick = async (task) => {
    if ((isStakeholder() || isClient()) && task?.userId !== accountData?.id) return;
    // Open drawer immediately
    setAction('update');
    setSelected(null); // Clear previous selection
    handleDrawerOpen();

    // Show loading inside drawer
    setIsDrawerLoading(true);

    try {
      const response = await getTaskById(task.id);
      const taskData = response?.data?.data;
      if (taskData) {
        const updatedTaskData = {
          ...taskData,
          consumedHours: task.consumedHours,
        };
        setSelected(updatedTaskData);
      } else {
        toast.error('Task data not found');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDrawerLoading(false);
    }
  };

  // Closing the drawer and resetting selected task
  const handleDrawerClose = () => {
    setSelected(null);
    setIsOpen(false);
    setIsDrawerLoading(false);
  };

  // Configuration for CSV export

  // Define columns
  const columns = useMemo(() => {
    const userIsStakeholder = isStakeholder();
    const hiddenColumnsForStakeholder = ['user.userName', 'daysDelayed', 'isBillable', 'plannedHours', 'consumedHours'];

    const baseColumns = TASK_LIST_TABLE_COLUMNS.filter((column) => {
      if (userIsStakeholder && hiddenColumnsForStakeholder.includes(column.accessorKey)) {
        return false;
      }
      return true;
    });

    return [
      ...baseColumns,
      {
        accessorKey: 'classification',
        header: 'Classification',
        enableSorting: false,
        filterVariant: 'select',
        filterSelectOptions: classificationsOption?.map((option) => ({
          value: option.id,
          label: option.name,
        })),
        Header: boldHeader('#454F5B'),
        Cell: ({ row }) => (row?.original?.classification?.id ? row?.original?.classification.name : 'N/A'),
        filterFn: (row, columnId, filterValue) => {
          return row.original.classification?.id === filterValue;
        },
      },
    ];
  }, [classificationsOption]);

  const table = useMaterialReactTable({
    columns,
    data: rows?.filter((task) => task?.taskName?.trim()?.toLowerCase() !== 'not aware'),
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    muiToolbarAlertBannerProps: isError
      ? {
          color: 'error',
          children: 'Error loading data',
        }
      : undefined,
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      showSkeletons: isRefetching,
      showAlertBanner: isError,
    },
    enableRowActions: isAdmin(),
    rowCount: count ?? 0,
    renderRowActions: ({ row }) => <Actions row={row} onDeleteIconClick={onDeleteIconClick} />,
    renderTopToolbarCustomActions: () => (
      <TopToolBar
        columnFilters={columnFilters}
        globalFilter={globalFilter}
        onCreateClick={handleCreate}
        sorting={sorting}
      />
    ),
    muiTableBodyRowProps: ({ row }) => ({
      sx: { backgroundColor: '#fff', cursor: 'pointer' },
      onClick: () => {
        handleEditCellClick(row.original);
      },
    }),
    ...TASK_LIST_TABLE_DEFAULT_PROPS,
  });

  return (
    <>
      <TableWrapper>
        <MaterialReactTable table={table} />
      </TableWrapper>
      <DeleteModal deleteSelected={deleteSelected} onDelete={onTaskDelete} onClose={handleDeleteCancel} />
      <Drawer
        PaperProps={{
          sx: {
            width: '55%',
          },
        }}
        anchor="right"
        open={isOpen}
        onClose={handleDrawerClose}
      >
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          {isDrawerLoading ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Loader size={40} />
            </Box>
          ) : (
            <Box sx={{ height: '100%', overflow: 'auto', paddingBottom: '20px' }}>
              <ProjectAddTask
                selectedTask={selected}
                open={isOpen}
                close={handleDrawerClose}
                onAddTask={handleCreate}
                action={action}
                onSubmit={refetch}
                projectName={projectName}
                canAddParentTask={canAddParentTask}
              />
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

ProjectTaskListTable.propTypes = {
  projectName: PropTypes.string,
};

export default ProjectTaskListTable;
